/**
 * Estado do módulo de estações (Zustand). Registra o codec de URL `s` no
 * import — `ui/modules.tsx` importa este arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { Dados, Estilo, Situacao } from './types';

export const DEFAULT_ESTILO: Estilo = {
  // 7 px (era 5; 09/10/2026, pedido do usuário): o mesmo tamanho base da qualidade do ar e do
  // nível dos rios. Com 5, a estação seca (0,62×) e a sem dado (0,56×) tinham ~3 px de raio e
  // sumiam no enquadramento da Amazônia inteira.
  tamanho: 7,
  opacidade: 0.9,
  // Todas as estações aparecem por padrão: ver onde NÃO choveu, e onde
  // ninguém mediu, é parte da leitura. Quem quiser menos ruído desliga.
  mostrarSemDado: true,
};

export const LIMITES = {
  tamanho: { min: 3, max: 12, step: 0.5 },
  opacidade: { min: 0.2, max: 1, step: 0.05 },
} as const;

/** A camada começa desligada: é dado extra, não o assunto principal do app. */
export const DEFAULT_VISIVEL = false;

const situacaoInicial: Situacao = { estado: 'idle', mensagem: null, erro: null };

const DADOS_VAZIOS: Dados = {
  estacoes: [],
  grade: null,
  linhaDe: new Map(),
  gerado: null,
  modo: null,
};

export interface StationState {
  visivel: boolean;
  estilo: Estilo;
  situacao: Situacao;
  dados: Dados;

  setVisivel: (on: boolean) => void;
  setEstilo: (patch: Partial<Estilo>) => void;
  setSituacao: (s: Situacao) => void;
  setDados: (d: Dados) => void;
}

function snap(v: number, { min, max, step }: { min: number; max: number; step: number }): number {
  if (!Number.isFinite(v)) return min;
  const c = Math.min(max, Math.max(min, v));
  return Math.round(Math.round(c / step) * step * 1000) / 1000;
}

export const useStationStore = create<StationState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  estilo: DEFAULT_ESTILO,
  situacao: situacaoInicial,
  dados: DADOS_VAZIOS,

  setVisivel: (visivel) => set({ visivel }),
  setEstilo: (patch) =>
    set((s) => ({
      estilo: {
        ...s.estilo,
        ...patch,
        ...(patch.tamanho !== undefined ? { tamanho: snap(patch.tamanho, LIMITES.tamanho) } : {}),
        ...(patch.opacidade !== undefined ? { opacidade: snap(patch.opacidade, LIMITES.opacidade) } : {}),
      },
    })),
  setSituacao: (situacao) => set({ situacao }),
  setDados: (dados) => set({ dados }),
}));

// ---------------------------------------------------------------------------
// Link compartilhável — chave `s`
// ---------------------------------------------------------------------------

/**
 * Formato: `1` ligada, mais os campos que diferem do padrão
 * (`t` tamanho, `o` opacidade, `v` inverte "mostrar sem dado").
 * Ex.: `s=1t7`. Desligada (o padrão) omite o parâmetro.
 *
 * `v` é sempre "o contrário do padrão", nunca um valor fixo: assim o link
 * continua curto e correto mesmo se o padrão mudar — foi o que aconteceu
 * quando as estações sem dado passaram a aparecer por padrão.
 */
export function encodeStations(s: Pick<StationState, 'visivel' | 'estilo'>): string | null {
  if (!s.visivel) return null;
  let out = '1';
  if (s.estilo.tamanho !== DEFAULT_ESTILO.tamanho) out += `t${s.estilo.tamanho}`;
  if (s.estilo.opacidade !== DEFAULT_ESTILO.opacidade) out += `o${s.estilo.opacidade}`;
  if (s.estilo.mostrarSemDado !== DEFAULT_ESTILO.mostrarSemDado) out += 'v';
  return out;
}

/** Tolera lixo: o que não for reconhecido fica no padrão. */
export function decodeStations(value: string): Pick<StationState, 'visivel' | 'estilo'> | null {
  if (!value || value[0] !== '1') return null;
  const tamanho = Number(/t([\d.]+)/.exec(value)?.[1]);
  const opacidade = Number(/o([\d.]+)/.exec(value)?.[1]);
  return {
    visivel: true,
    estilo: {
      tamanho: Number.isFinite(tamanho) ? snap(tamanho, LIMITES.tamanho) : DEFAULT_ESTILO.tamanho,
      opacidade: Number.isFinite(opacidade) ? snap(opacidade, LIMITES.opacidade) : DEFAULT_ESTILO.opacidade,
      mostrarSemDado: /v/.test(value.slice(1).replace(/[\d.]/g, ''))
        ? !DEFAULT_ESTILO.mostrarSemDado
        : DEFAULT_ESTILO.mostrarSemDado,
    },
  };
}

registerUrlCodec({
  key: 's',
  encode: () => encodeStations(useStationStore.getState()),
  decode: (value) => {
    const s = decodeStations(value);
    if (s) useStationStore.setState(s);
  },
  subscribe: (onChange) =>
    useStationStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.estilo !== b.estilo) onChange();
    }),
});
